import React, { useEffect, useMemo, useRef, useState } from "react";

import "react-medium-image-zoom/dist/styles.css";
import { Button } from "@/components/ui/button";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import LayerRenderer from "@/components/layers/LayerRenderer";
import { drawHoveredComponentOverlay } from "@/lib/hovered-component-overlay";
import { defaultHeight, defaultWidth } from "@/constants/image";
import { Crop, Maximize2, ScanSearch, Scissors } from "lucide-react";
import { SlicingBadge, SlicingOverlay } from "@/components/evaluation/slicing-overlay";
import { usePizzaSlicing } from "@/hooks/use-pizza-slicing";
import { ObstacleBadge, ObstacleOverlay } from "@/components/evaluation/obstacle-overlay";
import { usePizzaObstacles } from "@/hooks/use-pizza-obstacles";
import { isObstacleNok, storedObstacleCheck } from "@/lib/obstacles";
import { useLocalization } from "@/context/localization-context";

type PizzaEvaluationCVisualProps = object;

const PizzaEvaluationVisual = React.forwardRef<
  HTMLDivElement,
  PizzaEvaluationCVisualProps & React.HTMLAttributes<HTMLDivElement>
>(() => {
  const { t } = useLocalization();
  const pizzaCanvas = useRef<HTMLCanvasElement>(null);
  const hoveredClassCanvasRef = useRef<HTMLCanvasElement>(null);
  const {
    pizzaImage,
    layers,
    hoveredClass,
    sauceMask,
    hoveredHeatmapImage,
    selectedPizzaId,
    metadata,
    paddedPizzaRoi,
    applyRoi,
    setApplyRoi,
  } = usePizzaEvaluationContext();
  // Slicing layer (metadata.slicing_model), toggled with the scissors button
  const [showSlicing, setShowSlicing] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);
  const { slicing } = usePizzaSlicing(selectedPizzaId, metadata?.slicing_model, showSlicing && !!metadata);
  // Obstacle layer (photo_quality_evaluation.checks.obstacle_in_pizza), toggled with its button; shown
  // right away on a photo rejected for an obstacle
  const storedObstacles = storedObstacleCheck(metadata);
  const obstacleNok = isObstacleNok(storedObstacles);
  const [showObstacles, setShowObstacles] = useState(false);
  const [hoveredObstacle, setHoveredObstacle] = useState<string | null>(null);
  useEffect(() => setShowObstacles(obstacleNok), [selectedPizzaId, obstacleNok]);
  const { obstacles } = usePizzaObstacles(selectedPizzaId, storedObstacles, showObstacles && !!metadata);
  const slicingFrame = paddedPizzaRoi
    ? paddedPizzaRoi
    : pizzaImage
      ? { x: 0, y: 0, width: pizzaImage.width, height: pizzaImage.height }
      : null;

  useEffect(() => {
    const pizzaCanvasElement = pizzaCanvas.current;
    if (!pizzaCanvasElement || !paddedPizzaRoi) return;
    const ctx = pizzaCanvasElement.getContext("2d");
    if (!ctx || !pizzaImage) return;

    const { x, y, width, height } = paddedPizzaRoi;

    pizzaCanvasElement.width = width;
    pizzaCanvasElement.height = height;

    // ctx.clearRect(0, 0, pizzaCanvasElement.width, pizzaCanvasElement.height);
    ctx.drawImage(
      pizzaImage,
      x,
      y,
      width,
      height,
      0,
      0,
      pizzaCanvasElement.width,
      pizzaCanvasElement.height,
    );
  }, [pizzaCanvas, pizzaImage, paddedPizzaRoi]);

  useEffect(() => {
    const hoveredCanvas = hoveredClassCanvasRef.current;
    if (!hoveredCanvas || !paddedPizzaRoi) return;
    const ctx = hoveredCanvas.getContext("2d");
    if (!ctx) return;
    hoveredCanvas.width = paddedPizzaRoi.width;
    hoveredCanvas.height = paddedPizzaRoi.height;
    ctx.clearRect(0, 0, hoveredCanvas.width, hoveredCanvas.height);
    drawHoveredComponentOverlay(ctx, {
      hoveredClass,
      metadata,
      paddedRoi: paddedPizzaRoi,
      sauceMask,
      cacheKey: selectedPizzaId ?? "",
      hoveredHeatmap: hoveredHeatmapImage,
    });
  }, [hoveredClass, hoveredHeatmapImage, metadata, paddedPizzaRoi, sauceMask, selectedPizzaId]);

  const layerComponents = useMemo(() => {
    return layers?.map((layer) => (
      <LayerRenderer key={layer.name} layer={layer} />
    ));
  }, [layers, pizzaImage]);

  return (
    <div
      className="relative gap-4 w-full transition-all"
      style={{
        height: "calc-size(var(--intrinsic-size), size)",
        aspectRatio: paddedPizzaRoi
          ? paddedPizzaRoi.width / paddedPizzaRoi.height
          : defaultWidth / defaultHeight,
      }}
    >
      {/* <canvas
        className="w-full h-full opacity-100"
        ref={pizzaCanvas}
        style={{
          aspectRatio: paddedPizzaRoi
            ? paddedPizzaRoi.width / paddedPizzaRoi.height
            : 1,
        }}
      /> */}

      {layerComponents}

      <canvas
        ref={hoveredClassCanvasRef}
        className="absolute inset-0 w-full z-50"
      />

      {showSlicing && slicing && slicingFrame && (
        <div key={`${selectedPizzaId}-${slicing.version}`} className="pointer-events-none absolute inset-0 z-50">
          <SlicingOverlay slicing={slicing} frame={slicingFrame} hovered={hoveredSlice} onHover={setHoveredSlice} />
          <SlicingBadge slicing={slicing} className="bottom-14" />
        </div>
      )}

      {showObstacles && obstacles && slicingFrame && (
        <div
          key={`${selectedPizzaId}-${obstacles.details?.segmentation?.version}`}
          className="pointer-events-none absolute inset-0 z-50"
        >
          <ObstacleOverlay check={obstacles} frame={slicingFrame} hovered={hoveredObstacle} onHover={setHoveredObstacle} />
          <ObstacleBadge check={obstacles} className={showSlicing && slicing ? "bottom-24" : "bottom-14"} />
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 z-50 m-2">
        <Button
          size="sm"
          variant="secondary"
          className="pointer-events-auto absolute right-0 top-0 bg-slate-950/70 text-white backdrop-blur-md hover:bg-slate-950/85"
          onClick={() => setApplyRoi((prev) => !prev)}
        >
          {applyRoi ? <Maximize2 /> : <Crop />}
          {applyRoi ? t("detail.viewer.wholeFrame") : t("detail.viewer.cropToPizza")}
        </Button>
        {metadata && (
          <div className="pointer-events-auto absolute bottom-0 left-0 flex gap-2">
            <Button
              size="sm"
              variant={showSlicing ? "default" : "secondary"}
              onClick={() => setShowSlicing((prev) => !prev)}
            >
              <Scissors />
              {t("slicing.tab")}
            </Button>
            <Button
              size="sm"
              variant={showObstacles ? "default" : "secondary"}
              className="relative"
              onClick={() => setShowObstacles((prev) => !prev)}
            >
              <ScanSearch />
              {t("obstacle.tab")}
              {obstacleNok && (
                <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-background" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

export default PizzaEvaluationVisual;
